import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScrollText, Edit3, Loader2 } from "lucide-react";

/**
 * Editable text block tied to a settings key. Read is public; write is elder/admin only.
 */
export const EditableTextBlock = ({ settingKey, title, description, placeholder, canEdit, testIdPrefix = "block", emptyText }) => {
  const { API } = useAuth();
  const { t } = useLang();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/${settingKey}`);
      setValue(data.value || "");
    } catch { setValue(""); }
    finally { setLoaded(true); }
  }, [API, settingKey]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/${settingKey}`, { value: draft });
      setValue(draft);
      setOpen(false);
      toast.success(t("saved"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <section className="heritage-card p-6 mb-8" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-[#993D22]" />
          <div>
            <div className="label-uppercase text-[#993D22]">{title}</div>
            {description && <p className="text-xs text-[#6E5B4F] mt-0.5">{description}</p>}
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(value); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="border-[#993D22] text-[#993D22]" data-testid={`${testIdPrefix}-edit-btn`}>
                <Edit3 className="w-4 h-4 mr-1" /> {t("editTradition").replace("récit", "texte")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{title}</DialogTitle>
                {description && <DialogDescription className="text-sm text-[#6E5B4F]">{description}</DialogDescription>}
              </DialogHeader>
              <Textarea
                rows={10}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={placeholder}
                data-testid={`${testIdPrefix}-textarea`}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                <Button className="btn-terra" onClick={save} disabled={saving} data-testid={`${testIdPrefix}-save-btn`}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {t("save")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {loaded && (
        value ? (
          <p className="whitespace-pre-wrap text-[#3A2312] leading-relaxed" data-testid={`${testIdPrefix}-content`}>{value}</p>
        ) : (
          <p className="italic text-[#6E5B4F] text-sm" data-testid={`${testIdPrefix}-empty`}>{emptyText || t("emptyTradition")}</p>
        )
      )}
    </section>
  );
};

export default EditableTextBlock;
